import { forwardRef } from 'react';
import type { MantineNumberSize } from '@mantine/core';
import {
  // eslint-disable-next-line no-restricted-imports
  Text as MantineText,
  createPolymorphicComponent,
  createStyles,
} from '@mantine/core';

import { mainTheme } from '~/config/theme';

import type { TextProps } from './Text';

export const titleStyles = {
  'headline-xxxl': {
    fontSize: 32,
    fontWeight: 900,
    lineHeight: 1.25,
  },
  'headline-xxl': {
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1.3333333333333333,
  },
  'headline-xxl-bold': {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '32px',
    fontStyle: 'normal',
    letterSpacing: '-0.72px',
  },
  'headline-xl': {
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1.5,
  },
  'headline-large': {
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.5,
  },
  'headline-medium': {
    fontSize: 14,
    fontWeight: 900,
    lineHeight: '20px',
  },
  'headline-medium-xbold': {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: '20px',
  },
  'headline-small': {
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1.5,
  },
  'headline-x-small': {
    fontSize: 10,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  'headline-xx-small': {
    fontSize: 8,
    fontWeight: 900,
    lineHeight: 1.25,
  },
  'subhead-x-large': {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 1.3333333333333333,
  },
  'subhead-x-large-bold': {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '24px',
    letterSpacing: '-0.02em',
  },
  'subhead-large': {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.5,
  },
  'subhead-large-xbold': {
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.5,
    letterSpacing: '-0.32px',
  },
  'subhead-medium': {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4285714285714286,
  },
  'subhead-small': {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.6666666666666667,
  },
  'subhead-small-xbold': {
    fontSize: 12,
    fontWeight: 800,
    lineHeight: '20px',
  },
  'subhead-x-small': {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '12px',
  },
  'subhead-xx-small': {
    fontSize: 8,
    fontWeight: 600,
    lineHeight: 1.5,
  },
  'title-xxl': {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.3,
    fontStretch: '-3%',
  },
  'title-x-large': {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.2,
    fontStretch: '-3%',
  },
  'title-large': {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.375,
  },
  'title-medium': {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4285714285714286,
  },
  'title-small': {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  'display-headline-condensed-xxl': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 24,
    lineHeight: '32px',
    fontStretch: '75%',
    fontWeight: 900,
  },
  'display-headline-condensed-xl': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 20,
    lineHeight: '30px',
    fontStretch: '75%',
    fontWeight: 900,
  },
  'display-headline-condensed-large': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 16,
    lineHeight: '24px',
    fontStretch: '75%',
    fontWeight: 900,
  },
  'display-headline-condensed-medium': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 14,
    lineHeight: '20px',
    fontStretch: '75%',
    fontWeight: 900,
  },
  'display-headline-condensed-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 12,
    lineHeight: '18px',
    fontStretch: '75%',
    fontWeight: 900,
  },
  'display-headline-condensed-x-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 10,
    lineHeight: '12px',
    fontStretch: '75%',
    fontWeight: 900,
  },
  'display-headline-condensed-xx-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 8,
    lineHeight: '10px',
    fontStretch: '75%',
    fontWeight: 900,
  },

  'display-headline-expanded-xxxl': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 44,
    lineHeight: '48px',
    letterSpacing: '-0.02em',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-xxl': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 24,
    lineHeight: '32px',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-xl': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 20,
    lineHeight: '30px',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-large': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 16,
    lineHeight: '24px',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-medium': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 14,
    lineHeight: '20px',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 12,
    lineHeight: '18px',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-x-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 10,
    lineHeight: '12px',
    fontStretch: '125%',
    fontWeight: 900,
  },
  'display-headline-expanded-xx-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 8,
    lineHeight: '10px',
    fontStretch: '125%',
    fontWeight: 900,
  },

  'display-body-x-large': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 16,
    fontStretch: '75%',
    lineHeight: '22px',
    fontWeight: 400,
  },
  'display-body-large': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 14,
    fontStretch: '75%',
    lineHeight: '20px',
    fontWeight: 400,
  },
  'display-body-medium': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 12,
    fontStretch: '75%',
    lineHeight: '18px',
    fontWeight: 400,
  },
  'display-body-small': {
    fontFamily: mainTheme.other.fontFamily.hubotSans,
    fontSize: 10,
    fontStretch: '75%',
    lineHeight: '14px',
    fontWeight: 400,
  },
} as const;

export type TitleProps = {
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  variant?:
    | keyof typeof titleStyles
    | {
        base?: keyof typeof titleStyles;
        sm?: keyof typeof titleStyles;
        md?: keyof typeof titleStyles;
        lg?: keyof typeof titleStyles;
      };
} & Omit<TextProps, 'tag' | 'variant'>;

const useTitleStyles = createStyles((theme, { variant }: TitleProps) => ({
  title: {
    margin: 0,
    ...(() => {
      if (!variant) {
        return {};
      }

      if (typeof variant === 'string') {
        return titleStyles[variant];
      }

      return Object.entries(variant).reduce((acc, [key, value]) => {
        if (key === 'base') {
          return { ...acc, ...titleStyles[value] };
        }

        return {
          ...acc,
          [theme.fn.largerThan(key as MantineNumberSize)]: {
            ...titleStyles[value],
          },
        };
      }, {});
    })(),
  },
}));

const TitleFactory = forwardRef<HTMLHeadingElement, TitleProps>(
  ({ tag, variant, className, ...props }, ref) => {
    const { cx, classes } = useTitleStyles({ variant });

    return (
      <MantineText component={tag} ref={ref} className={cx(classes.title, className)} {...props} />
    );
  }
);
TitleFactory.displayName = 'Title';

export const Title = createPolymorphicComponent<'h1', TitleProps>(TitleFactory);
